/**
 * SessionIdleModal — the "still there?" countdown shown ~2 minutes before the
 * idle timeout signs the user out (policy: server/src/lib/sessionConfig.ts,
 * client enforcement: lib/sessionManager.ts). Mounted once in Layout so it can
 * appear over any authenticated surface.
 *
 * "Stay signed in" slides the session's idle window (a refresh); "Sign out now"
 * ends the session immediately. If the countdown reaches zero the manager's
 * next tick performs the sign-out — the modal never has to.
 */
import { useEffect, useState } from 'react';
import { useIdleWarningStore, staySignedIn } from '../lib/sessionManager';
import { useAuthStore } from '../store/useAuthStore';

export default function SessionIdleModal() {
  const deadline = useIdleWarningStore((s) => s.deadline);
  const logout = useAuthStore((s) => s.logout);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (deadline === null) return;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [deadline]);

  if (deadline === null) return null;

  const remainingMs = Math.max(0, deadline - now);
  const mm = Math.floor(remainingMs / 60_000);
  const ss = Math.floor((remainingMs % 60_000) / 1000);

  function signOutNow() {
    logout();
    window.location.href = '/login';
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
      <div className="bg-card rounded-lg shadow-xl p-6 w-full max-w-sm text-center">
        <div className="mx-auto mb-3 w-10 h-10 rounded-full bg-wash flex items-center justify-center" aria-hidden="true">
          <svg className="w-5 h-5 text-brand-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h2 className="text-lg font-semibold mb-1">Still there?</h2>
        <p className="text-sm text-ink-muted mb-1">
          You've been inactive for a while, so we'll sign you out to keep your account secure.
        </p>
        <p className="text-2xl font-semibold tabular-nums my-3" role="timer" aria-live="polite">
          {mm}:{String(ss).padStart(2, '0')}
        </p>
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={signOutNow}
            className="flex-1 border border-line text-ink-muted py-2 rounded text-sm hover:bg-wash">
            Sign out now
          </button>
          <button type="button" onClick={staySignedIn} autoFocus
            className="flex-1 bg-brand-primary text-white py-2 rounded text-sm hover:bg-brand-primary-dark">
            Stay signed in
          </button>
        </div>
      </div>
    </div>
  );
}
